import { Loader2, AlertCircle, Inbox } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn, getStatusMeta, getSignalMeta } from '../../lib/utils';

// ─── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 16, className }) {
  return <Loader2 size={size} className={cn('animate-spin text-txt-muted', className)} />;
}

// ─── Status Badge ──────────────────────────────────────────────────────────────
export function StatusBadge({ status, className }) {
  const meta = getStatusMeta(status);
  return (
    <span className={cn('badge border', meta.bg, meta.color, className)}>
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full',
          meta.dot,
          meta.pulse && 'animate-pulse'
        )}
      />
      {meta.label}
    </span>
  );
}

// ─── Signal Badge ──────────────────────────────────────────────────────────────
export function SignalBadge({ direction, className }) {
  const meta = getSignalMeta(direction);
  return (
    <span className={cn('badge border', meta.bg, meta.color, className)}>
      {meta.label}
    </span>
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, accent, icon: Icon, trend, className }) {
  return (
    <div className={cn('card p-4 flex flex-col gap-1', className)}>
      <div className="flex items-center justify-between">
        <span className="text-2xs text-txt-label uppercase tracking-widest">{label}</span>
        {Icon && <Icon size={13} className="text-txt-muted" />}
      </div>
      <div className={cn('text-2xl font-display font-bold tabular-nums leading-none', accent || 'text-txt-primary')}>
        {value ?? '—'}
      </div>
      {sub && <div className="text-2xs text-txt-muted font-mono">{sub}</div>}
      {trend != null && (
        <div className={cn('text-xs font-mono', trend >= 0 ? 'text-bull' : 'text-bear')}>
          {trend >= 0 ? '+' : ''}{trend}%
        </div>
      )}
    </div>
  );
}

// ─── Empty State ───────────────────────────────────────────────────────────────
export function EmptyState({ title = 'No data', description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <Inbox size={32} className="text-txt-muted opacity-40" />
      <div>
        <p className="text-sm font-medium text-txt-secondary">{title}</p>
        {description && <p className="text-xs text-txt-muted mt-1">{description}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Error State ───────────────────────────────────────────────────────────────
export function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <AlertCircle size={28} className="text-bear opacity-60" />
      <p className="text-sm text-txt-secondary">{message || 'Failed to load'}</p>
      {onRetry && (
        <button onClick={onRetry} className="btn-ghost text-xs">
          Retry
        </button>
      )}
    </div>
  );
}

// ─── Section Header ────────────────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, actions, className }) {
  return (
    <div className={cn('flex items-center justify-between mb-4', className)}>
      <div>
        <h2 className="font-display font-semibold text-sm text-txt-primary tracking-wide">{title}</h2>
        {subtitle && <p className="text-xs text-txt-secondary mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ─── Data Table ────────────────────────────────────────────────────────────────
export function DataTable({ columns, data, onRowClick, emptyText, loading }) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={20} />
      </div>
    );
  }

  if (!data?.length) {
    return <EmptyState title={emptyText || 'No records found'} />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-bg-border">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'text-left py-2 px-3 text-txt-label uppercase tracking-widest text-2xs font-semibold',
                  col.mobileHidden && 'hidden sm:table-cell',
                  col.hideUntilXl && 'hidden xl:table-cell'
                )}
                style={{ width: col.width }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id ?? i}
              onClick={() => onRowClick?.(row)}
              className={cn(
                'border-b border-bg-border/50 transition-colors',
                onRowClick && 'cursor-pointer hover:bg-bg-hover'
              )}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    'py-2.5 px-3 text-txt-secondary font-mono',
                    col.mobileHidden && 'hidden sm:table-cell',
                    col.hideUntilXl && 'hidden xl:table-cell'
                  )}
                >
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger,
  onConfirm,
  onCancel,
  submitting = false,
  submittingLabel,
}) {
  if (!open) return null;
  const confirmText = submitting ? (submittingLabel || 'Working…') : confirmLabel;
  const modalNode = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="card w-full max-w-sm animate-fade-in shadow-panel flex flex-col max-h-[calc(100dvh-2rem)] my-auto">
        <div className="p-6 shrink-0">
          <h3 className="font-display font-semibold text-txt-primary mb-2">{title}</h3>
        </div>
        <div className="px-6 overflow-y-auto">
          <p className="text-sm text-txt-secondary font-mono">{message}</p>
        </div>
        <div className="p-6 pt-4 shrink-0 flex justify-end gap-3">
          <button onClick={onCancel} disabled={submitting} className="btn-ghost disabled:opacity-50 disabled:cursor-not-allowed">{cancelLabel}</button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className={cn(
              danger ? 'btn-danger' : 'btn-primary',
              'inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed'
            )}
          >
            {submitting && <Spinner size={13} className="text-current" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
  if (typeof document === 'undefined') return modalNode;
  return createPortal(modalNode, document.body);
}

// ─── Tabs ──────────────────────────────────────────────────────────────────────
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 border-b border-bg-border mb-4">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={cn(
            'px-4 py-2 text-xs font-mono font-medium border-b-2 -mb-px transition-colors',
            active === tab.id
              ? 'border-amber text-amber'
              : 'border-transparent text-txt-secondary hover:text-txt-primary'
          )}
        >
          {tab.label}
          {tab.count != null && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-bg-hover text-2xs">{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Pager ─────────────────────────────────────────────────────────────────────
export function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-4">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="btn-ghost text-xs disabled:opacity-30"
      >
        ← Prev
      </button>
      <span className="text-xs font-mono text-txt-muted">
        {page} / {totalPages}
      </span>
      <button
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="btn-ghost text-xs disabled:opacity-30"
      >
        Next →
      </button>
    </div>
  );
}
